import { useEffect, useRef } from 'react';
import { btns } from './buttons';
import { cls } from './formStyles';

/**
 * The card a person sees after "Sign out" on the snapshot or the onboarding form
 * (crossmarc/steward-app#2432). The caller decides whether to show it and passes the words, so
 * this file holds only the shape: the firm and the form's name, the heading (which takes focus
 * on mount, as the other forms' headings do), what happened to the form, and the way back.
 */
export const SIGNED_OUT_HEADING = 'You are signed out.';
export const SIGNED_OUT_NOT_SENT = 'Your progress is saved. To carry on, sign in with the same email address.';
export const SIGNED_OUT_SIGN_IN_AGAIN = 'Sign in again';

/** "Thank you. Your {noun} is with {firm}. You can close this window." */
export function signedOutSentLine(noun: string, firm: string): string {
  return `Thank you. Your ${noun} is with ${firm}. You can close this window.`;
}

/** "Thank you. Your documents are with {firm}. You can close this window." (signed out from the requests page, #2473). */
export function signedOutDocumentsLine(firm: string): string {
  return `Thank you. Your documents are with ${firm}. You can close this window.`;
}

export function SignedOutCard({ firmName, formName, sent, requests = false, noun, firmInSentence, onSignInAgain }: {
  /** The firm's name as the card's first line. */
  firmName: string;
  /** What the form is called, after the dot. */
  formName: string;
  sent: boolean;
  /** Signed out from the requests page: says the documents are with the firm, whatever `sent` is. */
  requests?: boolean;
  /** The form in the middle of a sentence ("financial snapshot", "form"). */
  noun: string;
  /** The firm as it reads in a sentence ("RIPL", or "your advisor"). */
  firmInSentence: string;
  onSignInAgain: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  return (
    <div
      data-testid="signed-out-card"
      className="mx-auto w-full max-w-[440px] bg-surface border border-line rounded-token p-8 text-center shadow-panel"
    >
      <div
        data-testid="signed-out-form"
        className="mb-4 break-words font-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-slate"
      >
        {firmName} · {formName}
      </div>
      <h1 ref={headingRef} tabIndex={-1} className={`${cls.h1} outline-none`}>{SIGNED_OUT_HEADING}</h1>
      <div className="mx-auto mt-4 h-px w-8 bg-bronze" />
      <p data-testid="signed-out-body" className="mt-6 mb-6 text-[13px] leading-relaxed text-slate">
        {requests ? signedOutDocumentsLine(firmInSentence) : sent ? signedOutSentLine(noun, firmInSentence) : SIGNED_OUT_NOT_SENT}
      </p>
      <button type="button" onClick={onSignInAgain} className={`${btns.btn} w-full min-h-[48px] text-[12px]`}>
        {SIGNED_OUT_SIGN_IN_AGAIN}
      </button>
    </div>
  );
}
